import React from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import FilterDropdown from "./FilterDropdown";
import PostAnalysisMultiSelectDropdown from "./PostAnalysisMultiSelectDropdown";
import CustomDropdown from "@/components/common-components/filter options/CustomDropdown";
import { Check } from "lucide-react";

export default function FilterBar({ filters, setFilters, filterOptions = {}, onLocalFiltersChange }) {
  // Local state for filters (not applied until Apply button is clicked)
  const [localFilters, setLocalFilters] = React.useState(filters);
  const [open, setOpen] = React.useState(false);
  const [range, setRange] = React.useState({
    from: filters.start_date ? new Date(filters.start_date) : null,
    to: filters.end_date ? new Date(filters.end_date) : null
  });

  // Sync local filters when prop filters change externally (e.g., clear all)
  React.useEffect(() => {
    setLocalFilters(filters);
    setRange({
      from: filters.start_date ? new Date(filters.start_date) : null,
      to: filters.end_date ? new Date(filters.end_date) : null
    });
  }, [filters]);

  // Debounce local filter changes to fetch updated filter options
  React.useEffect(() => {
    if (!onLocalFiltersChange) return;
    
    const timeoutId = setTimeout(() => {
      onLocalFiltersChange(localFilters);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [localFilters, onLocalFiltersChange]);
  
  // Handle local filter change (doesn't apply to actual filters)
  const handleLocalFilterChange = (key, value) => {
    setLocalFilters((prev) => {
      // For multi-select filters (arrays), don't convert "__all__" to empty string
      const isMultiSelect = key === "campaign" || key === "poc_id" || key === "poc_name";
      const newValue = (value === "__all__" && !isMultiSelect) ? "" : value;
      
      // Ensure arrays are always arrays, never empty strings
      const finalValue = isMultiSelect && !Array.isArray(newValue) 
        ? (newValue ? [newValue] : []) 
        : newValue;
      
      const newFilters = { ...prev, [key]: finalValue };
      
      // Cascade: Clear dependent filters when parent filter changes
      if (key === "brand") {
        // When brand changes, clear campaign, poc_id, and poc_name
        newFilters.campaign = [];
        newFilters.poc_id = [];
        newFilters.poc_name = [];
      } else if (key === "region") {
        // When region changes, clear campaign, poc_id, and poc_name
        newFilters.campaign = [];
        newFilters.poc_id = [];
        newFilters.poc_name = [];
      } else if (key === "campaign") {
        // When campaign changes, clear poc_id and poc_name
        newFilters.poc_id = [];
        newFilters.poc_name = [];
      } else if (key === "poc_name") {
        // When poc_name changes, clear poc_id
        newFilters.poc_id = [];
      }
      
      return newFilters;
    });
  };
  
  // Apply filters - update actual filters with local state
  const handleApplyFilters = () => {
    setFilters(localFilters);
  };
  
  // Check if there are pending changes
  const hasPendingChanges = React.useMemo(() => {
    const stringCompare = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
    const normalizeValue = (v) => {
      if (Array.isArray(v)) {
        return JSON.stringify([...v].sort(stringCompare));
      }
      return String(v || "");
    };
    
    return (
      normalizeValue(localFilters.brand) !== normalizeValue(filters.brand) ||
      normalizeValue(localFilters.region) !== normalizeValue(filters.region) ||
      normalizeValue(localFilters.campaign) !== normalizeValue(filters.campaign) ||
      normalizeValue(localFilters.poc_id) !== normalizeValue(filters.poc_id) ||
      normalizeValue(localFilters.poc_name) !== normalizeValue(filters.poc_name) ||
      normalizeValue(localFilters.key_account) !== normalizeValue(filters.key_account) ||
      normalizeValue(localFilters.start_date) !== normalizeValue(filters.start_date) ||
      normalizeValue(localFilters.end_date) !== normalizeValue(filters.end_date)
    );
  }, [localFilters, filters]);
  
  // Handle date range change (updates local state only)
  const handleRangeChange = (newRange) => {
    setRange(newRange);
    if (newRange?.from && newRange?.to) {
      setLocalFilters((prev) => ({
        ...prev,
        start_date: format(newRange.from, "yyyy-MM-dd"),
        end_date: format(newRange.to, "yyyy-MM-dd")
      }));
    } else {
      setLocalFilters((prev) => ({
        ...prev,
        start_date: "",
        end_date: ""
      }));
    }
  };

  // Filter config for dynamic rendering
  const filterConfig = [
    { key: "brand", label: "Brand" },
    { key: "region", label: "Region" },
    { key: "campaign", label: "Campaign", multi: true },
    { key: "poc_id", label: "POC ID", multi: true },
    { key: "poc_name", label: "POC Name", multi: true },
    { key: "key_account", label: "Key Account" },
  ];

  // Helper to get options for each filter
  const getOptions = (key) => {
    if (!filterOptions) return [];
    let rawOptions = [];
    if (key === "campaign") rawOptions = filterOptions.campaign_names || [];
    else if (key === "poc_id") rawOptions = filterOptions.poc_ids || [];
    else if (key === "poc_name") rawOptions = filterOptions.poc_names || [];
    else if (key === "key_account") rawOptions = filterOptions.key_accounts || [];
    else if (key === "brand") rawOptions = filterOptions.brands || [];
    else if (key === "region") rawOptions = filterOptions.regions || [];
    else rawOptions = filterOptions[`${key}s`] || [];
    
    // Ensure options are in {value, label} format
    return rawOptions.map(opt => {
      if (typeof opt === "string") {
        return { value: opt, label: opt };
      }
      return opt;
    });
  };

  return (
    <div className="mb-4">
      <div className="flex flex-wrap gap-4 items-end">
        {filterConfig.map(f =>
          f.multi || f.key === "poc_id" || f.key === "poc_name" || f.key === "campaign" ? (
            <PostAnalysisMultiSelectDropdown
              key={f.key}
              label={f.label}
              value={localFilters[f.key] || []}
              onChange={v => handleLocalFilterChange(f.key, v)}
              options={getOptions(f.key)}
              placeholder={`Select ${f.label}`}
              enableSearch={true}
              allowClearAll={false}
              applyOnClose={false} // Don't apply on close, use Apply button instead
              width={f.key === "campaign" ? "min-w-[300px]" : "min-w-[200px]"} // Wider for campaign names
            />
          ) : f.key === "key_account" ? (
            <CustomDropdown
              key={f.key}
              label={f.label}
              value={localFilters[f.key] || ""}
              onChange={v => handleLocalFilterChange(f.key, v)}
              options={getOptions(f.key)}
              placeholder={`Select ${f.label}`}
              width="min-w-[200px]"
              enableSearch={true}
            />
          ) : (
            <FilterDropdown
              key={f.key}
              label={f.label}
              value={localFilters[f.key]}
              onChange={v => handleLocalFilterChange(f.key, v)}
              options={getOptions(f.key)}
              multi={f.multi}
              triggerClassName="min-w-[200px] bg-background border border-border"
              contentClassName="w-full max-h-48 overflow-y-auto bg-background border border-border"
            />
          )
        )}
        {/* Date Range Picker */}
        <div className="flex flex-col space-y-1.5 min-w-[200px]">
          <label className="text-sm font-medium text-foreground leading-none mb-1">Date Range</label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-start text-left font-normal bg-background border border-border rounded-lg text-foreground"
              >
                {range.from && range.to
                  ? `${format(range.from, "MMM d, yyyy")} - ${format(range.to, "MMM d, yyyy")}`
                  : "Pick a date range"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-background border border-border" align="start">
              <Calendar
                mode="range"
                selected={range}
                onSelect={handleRangeChange}
                numberOfMonths={2}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        {/* Apply Filters Button - Inline with filters */}
        <div className="flex flex-col space-y-1.5">
          <label className="text-sm font-medium text-foreground leading-none mb-1 opacity-0 pointer-events-none">Apply</label>
          <Button
            onClick={handleApplyFilters}
            disabled={!hasPendingChanges}
            size="default"
            className={`min-w-[140px] h-9 font-medium transition-all ${
              hasPendingChanges 
                ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-sm hover:shadow-md border-2 border-primary' 
                : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50 border border-border'
            }`}
          >
            <Check className="h-4 w-4 mr-2" />
            Apply
            {hasPendingChanges && (
              <span className="ml-1.5 px-1.5 py-0.5 bg-primary-foreground/20 rounded-full text-[10px] font-bold leading-none">
                {(() => {
                  const stringCompare = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
                  const normalizeValue = (v) => {
                    if (Array.isArray(v)) {
                      return JSON.stringify([...v].sort(stringCompare));
                    }
                    return String(v || "");
                  };
                  
                  const filterKeys = ["brand", "region", "campaign", "poc_id", "poc_name", "key_account", "start_date", "end_date"];
                  return filterKeys.filter(key => {
                    const localVal = localFilters[key];
                    const filterVal = filters[key];
                    const normalizedLocal = normalizeValue(localVal);
                    const normalizedFilter = normalizeValue(filterVal);
                    // Only count if values are different AND local value is not empty
                    return normalizedLocal !== normalizedFilter && normalizedLocal !== "" && normalizedLocal !== "[]";
                  }).length;
                })()}
              </span>
            )}
          </Button>
      </div>
      </div>
      {/* Pending changes indicator */}
      {hasPendingChanges && (
        <div className="mt-3 flex items-center justify-end">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 bg-primary rounded-full animate-pulse"></span>
            Unsaved filter changes
          </span>
        </div>
      )}
    </div>
  );
} 